/*
  Holds the Image gallery still while one of its videos is playing.

  The section has two moving layouts — the Swiper carousel and the CSS-animated marquee
  — and both are stopped from here: the players report playback through
  `_EVENT_HELPER.videoPlaybackChange`, which bubbles, so this element only has to listen
  on itself rather than know which player types are inside it.

  The carousel needs more than one stop() call. Its autoplay is configured with
  pauseOnMouseEnter, so Swiper resumes on its own the moment the pointer leaves the
  section — and handleTouchEnd restarts it after a swipe. A single stop() is undone by
  either. The lock therefore stays armed: while a video plays, every attempt Swiper makes
  to start or resume autoplay is stopped again as it is announced.

  Players are counted rather than tracked with a boolean. Two videos can overlap (one
  autoplaying, one clicked), and a single flag would let the first one to stop release
  the carousel under the other.
*/
class GalleryPlaybackLock extends HTMLElement {
   connectedCallback() {
      this.playing = new Set();
      this.locked = false;
      this.autoplayWasRunning = false;

      this.onPlaybackChange = this.onPlaybackChange.bind(this);
      this.holdAutoplay = this.holdAutoplay.bind(this);
      this.addEventListener(_EVENT_HELPER.videoPlaybackChange, this.onPlaybackChange);
   }

   disconnectedCallback() {
      this.removeEventListener(_EVENT_HELPER.videoPlaybackChange, this.onPlaybackChange);
      this.releaseSwiper();
   }

   get swiper() {
      // Read late, never cached: the carousel can be initialised after this element
      // connects, and is re-initialised on breakpoint changes.
      return this.querySelector('carousel-component')?.swiperInstance || null;
   }

   onPlaybackChange(event) {
      const player = event.target;
      if (event.detail && event.detail.playing) {
         this.playing.add(player);
      } else {
         this.playing.delete(player);
      }
      this.setLocked(this.playing.size > 0);
   }

   setLocked(locked) {
      if (this.locked === locked) return;
      this.locked = locked;

      // The marquee is a CSS animation, so a class on the container is all it takes.
      this.classList.toggle('gallery--playback-paused', locked);

      if (locked) {
         this.holdSwiper();
      } else {
         this.releaseSwiper();
      }
   }

   holdSwiper() {
      const swiper = this.swiper;
      if (!swiper || !swiper.autoplay) return;

      // Remembered so the carousel is only restarted if it was actually running. With
      // autoplay switched off in the editor, swiper.autoplay still exists and calling
      // start() would set a carousel moving that the merchant asked to stay put.
      this.autoplayWasRunning = swiper.autoplay.running === true;
      if (!this.autoplayWasRunning) return;

      swiper.autoplay.stop();
      swiper.on('autoplayStart', this.holdAutoplay);
      swiper.on('autoplayResume', this.holdAutoplay);
      this.heldSwiper = swiper;
   }

   holdAutoplay() {
      if (!this.locked) return;
      this.heldSwiper?.autoplay?.stop();
   }

   releaseSwiper() {
      const swiper = this.heldSwiper;
      this.heldSwiper = null;
      if (!swiper || !swiper.autoplay) return;

      swiper.off('autoplayStart', this.holdAutoplay);
      swiper.off('autoplayResume', this.holdAutoplay);
      if (this.autoplayWasRunning) {
         swiper.autoplay.start();
      }
   }
}

customElements.get('gallery-playback-lock') ||
   customElements.define('gallery-playback-lock', GalleryPlaybackLock);

/*
  Opens a gallery image full screen.

  One delegated listener and one <dialog> per section, rather than per image: the
  carousel clones slides for its loop, so anything bound per image would be bound
  several times over for the same picture.

  <dialog>.showModal() is doing the heavy lifting — top layer, Escape, focus trapped and
  returned on close, the rest of the page inert. Reimplementing that by hand is where
  lightboxes usually go wrong.
*/
class GalleryLightbox extends HTMLElement {
   connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      // A drag across a carousel ends in a click. Comparing it to where the pointer went
      // down separates "opened the image" from "swiped past it".
      this.addEventListener('pointerdown', (event) => {
         this.pointerOrigin = { x: event.clientX, y: event.clientY };
      });
   }

   onClick(event) {
      const trigger = event.target.closest('[data-zoom-src]');
      if (!trigger || !this.contains(trigger)) return;
      if (this.pointerMoved(event)) return;
      this.open(trigger);
   }

   onKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const trigger = event.target.closest('[data-zoom-src]');
      if (!trigger || !this.contains(trigger)) return;
      event.preventDefault();
      this.open(trigger);
   }

   pointerMoved(event) {
      const origin = this.pointerOrigin;
      this.pointerOrigin = null;
      if (!origin) return false;
      return (
         Math.abs(event.clientX - origin.x) > 8 ||
         Math.abs(event.clientY - origin.y) > 8
      );
   }

   open(trigger) {
      const dialog = this.getDialog();
      const image = dialog.querySelector('.gallery-lightbox__img');
      image.src = trigger.dataset.zoomSrc;
      image.alt = trigger.dataset.zoomAlt || '';
      const ratio = parseFloat(trigger.dataset.zoomRatio);
      // Reserves the right shape while the full-size file loads, so the dialog does not
      // resize under the pointer once it arrives.
      image.style.aspectRatio = ratio > 0 ? String(ratio) : '';
      dialog.showModal();
   }

   getDialog() {
      if (this.dialogEl) return this.dialogEl;

      const dialog = document.createElement('dialog');
      dialog.className = 'gallery-lightbox';
      dialog.innerHTML = `
         <button type="button" class="gallery-lightbox__close" aria-label="Close">&times;</button>
         <img class="gallery-lightbox__img" src="" alt="">
      `;
      // Clicking the backdrop closes. The dialog element covers the whole viewport, so
      // "outside" means the click landed on the dialog itself and not on its contents.
      dialog.addEventListener('click', (event) => {
         if (event.target === dialog || event.target.closest('.gallery-lightbox__close')) {
            dialog.close();
         }
      });
      // Dropping the source frees the decoded image and stops a stale picture flashing
      // up the next time the dialog opens.
      dialog.addEventListener('close', () => {
         dialog.querySelector('.gallery-lightbox__img').src = '';
      });

      this.append(dialog);
      this.dialogEl = dialog;
      return dialog;
   }
}

customElements.get('gallery-lightbox') ||
   customElements.define('gallery-lightbox', GalleryLightbox);
